import React, { useEffect, useState } from "react";
import { useAuth } from "@/auth";
import { useI18n } from "@/i18n";
import { PAISES } from "@/data/countries";
import { findCountry, countryLabel, provinceLabel } from "@/components/GeoSelect";
import { Plus, Trash2, Power, Weight, Box, Package, Pencil, X, TrendingUp } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  origen: "", destino: "", puerto_origen: "", puerto_destino: "", provincia: "", nombre: "Meos Express",
  observaciones: "", moneda: "USD",
  precio_base: 0, precio_kg: 0, precio_cbm: 0, precio_pallet: 0,
  precio_20ft: 0, precio_40ft: 0, precio_40hc: 0,
  precio_documentos: 0, precio_pasaporte: 0, precio_goods: 0,
  precio_sucursal_kg: 0, precio_sucursal_cbm: 0, precio_sucursal_pallet: 0,
  precio_sucursal_20ft: 0, precio_sucursal_40ft: 0, precio_sucursal_40hc: 0,
  sucursal_pricing: [],
  merchandise_types: [],
  provincias_destino: [],
  provincia_required: false,
  provincia_pricing: [],
  brackets_kg: [], brackets_cbm: [], brackets_pallet: [],
  servicios_activos: { kg: true, cbm: true, pallets: true, "20ft": true, "40ft": true, "40hc": true, documentos: true, pasaporte: true, goods: true },
  dias: 3, modo: "todos", activa: true,
};

const CURRENCIES = ["USD", "EUR", "MXN", "CNY", "GBP", "JPY", "AED", "SAR"];

export default function RoutesPage() {
  const { api } = useAuth();
  const { t, lang } = useI18n();
  const [rows, setRows] = useState([]);
  const [oficinas, setOficinas] = useState([]);
  const [countries, setCountries] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);

  const load = () => api.get("/routes").then(r => setRows(r.data));
  useEffect(() => {
    load();
    api.get("/oficinas").then(r => setOficinas(r.data.oficinas || [])).catch(() => {});
    api.get("/countries").then(r => setCountries(r.data.filter(c => c.activo))).catch(() => {});
  }, []);

  // Localized country options: DB countries provide ES/EN/AR labels, the static
  // PAISES list keeps the full catalogue. Stored value stays the canonical Spanish name.
  const paisOptions = PAISES.map((p) => {
    const c = findCountry(countries, p);
    return { value: p, label: c ? countryLabel(c, lang) : p };
  }).sort((a, b) => a.label.localeCompare(b.label, lang === "ar" ? "ar" : lang));

  // Provinces of the destination country, offered as quick-add chips
  const destProvinceCatalog = findCountry(countries, form.destino)?.provinces || [];

  const openNew = () => { setForm(EMPTY); setEditingId(null); setShowForm(true); };

  const openEdit = (r) => {
    // Backward-compat for older routes without servicios_activos
    const servicios = r.servicios_activos || { kg: true, cbm: true, pallets: true, "20ft": true, "40ft": true, "40hc": true };
    setForm({ ...EMPTY, ...r, servicios_activos: servicios, sucursal_pricing: r.sucursal_pricing || [], merchandise_types: r.merchandise_types || [], provincias_destino: Array.from(new Set([...(r.provincias_destino || []), ...((r.provincia_pricing || []).map(p => p.provincia).filter(Boolean))])), provincia_required: !!r.provincia_required, provincia_pricing: r.provincia_pricing || [] });
    setEditingId(r.id);
    setShowForm(true);
  };

  const addBranchPrice = () => {
    setForm(f => ({ ...f, sucursal_pricing: [...(f.sucursal_pricing || []), { oficina: "", precio_base: 0, precio_kg: 0, precio_cbm: 0, precio_pallet: 0, precio_20ft: 0, precio_40ft: 0, precio_40hc: 0 }] }));
  };
  const updateBranchPrice = (idx, key, val) => {
    setForm(f => {
      const list = [...(f.sucursal_pricing || [])];
      list[idx] = { ...list[idx], [key]: typeof val === "number" ? val : val };
      return { ...f, sucursal_pricing: list };
    });
  };
  const removeBranchPrice = (idx) => setForm(f => ({ ...f, sucursal_pricing: f.sucursal_pricing.filter((_, i) => i !== idx) }));

  // ==== Dynamic merchandise types ====
  const addMerchType = () => setForm(f => ({ ...f, merchandise_types: [...(f.merchandise_types || []), { nombre: "", precio: 0, tipo: "envio", is_mandatory: false }] }));
  const updateMerchType = (idx, key, val) => setForm(f => {
    const list = [...(f.merchandise_types || [])];
    list[idx] = { ...list[idx], [key]: val };
    return { ...f, merchandise_types: list };
  });
  const removeMerchType = (idx) => setForm(f => ({ ...f, merchandise_types: (f.merchandise_types || []).filter((_, i) => i !== idx) }));

  const toggleService = (key) =>
    setForm(f => ({ ...f, servicios_activos: { ...f.servicios_activos, [key]: !f.servicios_activos[key] } }));

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) await api.patch(`/routes/${editingId}`, form);
      else await api.post("/routes", form);
      toast.success(t("saved"));
      setShowForm(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const toggleActive = async (r) => {
    await api.patch(`/routes/${r.id}`, { activa: !r.activa });
    load();
  };

  const remove = async (id) => {
    if (!window.confirm(t("confirm_delete"))) return;
    await api.delete(`/routes/${id}`);
    toast.success(t("deleted"));
    load();
  };

  return (
    <div className="space-y-5" data-testid="routes-page">
      <div className="flex justify-between items-start">
        <div>
          <h2 className="font-display text-lg font-semibold">{t("own_routes")}</h2>
          <p className="text-sm text-slate-500">{t("routes_note")}</p>
        </div>
        <button data-testid="new-route-btn" onClick={openNew} className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-semibold text-sm flex items-center gap-2 transition-colors">
          <Plus className="w-4 h-4" /> {t("new_route")}
        </button>
      </div>

      <div className="space-y-3">
        {rows.map(r => (
          <div key={r.id} data-testid={`route-${r.id}`} className="bg-white border border-slate-200 rounded-xl p-5">
            <div className="flex flex-wrap items-start justify-between gap-4 mb-4 pb-4 border-b border-slate-100">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-display font-semibold text-lg">{r.origen} → {r.destino}</h3>
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full font-semibold ${r.activa ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                    {r.activa ? t("active") : t("inactive")}
                  </span>
                  <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-semibold">
                    {r.modo === "todos" ? t("all_modes") : t(r.modo)}
                  </span>
                  <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-blue-100 text-red-700 font-semibold">
                    {r.moneda || "USD"}
                  </span>
                </div>
                <p className="text-sm text-slate-500 mt-0.5">{r.nombre} · {r.dias} {t("transit_days").toLowerCase()}</p>
                <p className="text-xs text-slate-500 mt-1">{t("price_base")}: <span className="font-semibold tabular-nums text-slate-700">${r.precio_base}</span></p>
              </div>
              <div className="flex gap-2">
                <button data-testid={`route-edit-${r.id}`} onClick={() => openEdit(r)} className="p-2 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" title={t("edit")}><Pencil className="w-4 h-4" /></button>
                <button onClick={() => toggleActive(r)} className="p-2 text-slate-500 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"><Power className="w-4 h-4" /></button>
                <button onClick={() => remove(r.id)} className="p-2 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>

            {/* 3 price columns */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <PriceBlock Icon={Weight} label={t("price_kg")} value={r.precio_kg} tone="blue" />
              <PriceBlock Icon={Box} label={t("price_cbm")} value={r.precio_cbm} tone="indigo" />
              <PriceBlock Icon={Package} label={t("price_pallet")} value={r.precio_pallet} tone="amber" />
            </div>
          </div>
        ))}
        {!rows.length && (
          <div className="bg-white border border-slate-200 rounded-xl p-12 text-center text-slate-400">
            <p>—</p>
          </div>
        )}
      </div>

      {/* Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="route-modal">
          <form onSubmit={submit} className="bg-white rounded-xl w-full max-w-2xl p-6 space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center">
              <h3 className="font-display font-semibold text-lg">{editingId ? t("edit_route") : t("new_route")}</h3>
              <button type="button" onClick={()=>setShowForm(false)} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Field label={t("origin")}>
                <select required data-testid="rf-origen" value={form.origen} onChange={(e)=>setForm({...form, origen: e.target.value})} className="input">
                  <option value="">{t("select_option")}</option>
                  {paisOptions.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </Field>
              <Field label={t("destination")}>
                <select required data-testid="rf-destino" value={form.destino} onChange={(e)=>setForm({...form, destino: e.target.value})} className="input">
                  <option value="">{t("select_option")}</option>
                  {paisOptions.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </Field>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Field label={t("port_origin")}>
                <input data-testid="rf-puerto-origen" value={form.puerto_origen} onChange={(e)=>setForm({...form, puerto_origen: e.target.value})} placeholder="Guangzhou, Shanghai, JFK..." className="input" />
              </Field>
              <Field label={t("port_dest")}>
                <input data-testid="rf-puerto-destino" value={form.puerto_destino} onChange={(e)=>setForm({...form, puerto_destino: e.target.value})} placeholder="Beirut, Latakia, MEX..." className="input" />
              </Field>
            </div>

            <Field label={t("province")}>
              <input data-testid="rf-provincia" value={form.provincia} onChange={(e)=>setForm({...form, provincia: e.target.value})} placeholder="Nuevo León, Antioquia, ... (opcional)" className="input" list="rf-prov-list" />
              <datalist id="rf-prov-list">
                {(findCountry(countries, form.origen)?.provinces || []).map(p => (
                  <option key={p.name} value={p.name}>{provinceLabel(p, lang)}</option>
                ))}
              </datalist>
            </Field>

            <Field label={t("service_name")}><input value={form.nombre} onChange={(e)=>setForm({...form, nombre: e.target.value})} className="input" /></Field>

            <div className="grid grid-cols-2 gap-3">
              <Field label={t("mode")}>
                <select value={form.modo} onChange={(e)=>setForm({...form, modo: e.target.value})} className="input">
                  <option value="todos">{t("all_modes")}</option>
                  <option value="aereo">{t("aereo")}</option>
                  <option value="terrestre">{t("terrestre")}</option>
                  <option value="maritimo">{t("maritimo")}</option>
                </select>
              </Field>
              <Field label={t("transit_days")}><input type="number" min="1" value={form.dias} onChange={(e)=>setForm({...form, dias: +e.target.value})} className="input" /></Field>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Field label={t("currency")}>
                <select data-testid="rf-moneda" value={form.moneda} onChange={(e)=>setForm({...form, moneda: e.target.value})} className="input">
                  {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
              <Field label={t("price_base") + ` (${form.moneda})`}>
                <input type="number" step="0.01" data-testid="rf-base" value={form.precio_base} onChange={(e)=>setForm({...form, precio_base: +e.target.value})} className="input" />
              </Field>
            </div>

            <div className="pt-2 border-t border-slate-100 space-y-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("base_prices_tiers")}</p>

              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-xs font-semibold text-red-900 mb-2">{t("services_enabled")}</p>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { k: "kg", label: t("price_kg_short") },
                    { k: "cbm", label: "CBM" },
                    { k: "pallets", label: t("cargo_pallets") },
                    { k: "20ft", label: "20ft" },
                    { k: "40ft", label: "40ft" },
                    { k: "40hc", label: "40HC" },
                    { k: "documentos", label: t("courier_documentos") },
                    { k: "pasaporte", label: t("courier_pasaporte") },
                    { k: "goods", label: t("courier_goods") },
                  ].map(s => {
                    const on = form.servicios_activos[s.k];
                    return (
                      <button
                        key={s.k}
                        type="button"
                        data-testid={`rf-svc-${s.k}`}
                        onClick={() => toggleService(s.k)}
                        className={`px-2 py-1.5 rounded-md text-xs font-semibold border-2 transition-colors ${
                          on ? "bg-blue-600 border-red-600 text-white" : "bg-white border-slate-300 text-slate-500 hover:border-slate-400"
                        }`}
                      >
                        {on ? "✓" : "×"} {s.label}
                      </button>
                    );
                  })}
                </div>
                <p className="text-[10px] text-red-800 mt-2">{t("services_hint")}</p>
              </div>

              <TierEditor
                testidPrefix="kg"
                Icon={Weight}
                label={t("price_kg")}
                unit="kg"
                base={form.precio_kg}
                brackets={form.brackets_kg}
                setBase={(v)=>setForm({...form, precio_kg: v})}
                setBrackets={(b)=>setForm({...form, brackets_kg: b})}
              />
              <TierEditor
                testidPrefix="cbm"
                Icon={Box}
                label={t("price_cbm")}
                unit="CBM"
                base={form.precio_cbm}
                brackets={form.brackets_cbm}
                setBase={(v)=>setForm({...form, precio_cbm: v})}
                setBrackets={(b)=>setForm({...form, brackets_cbm: b})}
              />
              <TierEditor
                testidPrefix="pallet"
                Icon={Package}
                label={t("price_pallet")}
                unit={t("cargo_pallets").toLowerCase()}
                base={form.precio_pallet}
                brackets={form.brackets_pallet}
                setBase={(v)=>setForm({...form, precio_pallet: v})}
                setBrackets={(b)=>setForm({...form, brackets_pallet: b})}
              />

              <div className="border border-slate-200 rounded-lg p-3 bg-slate-50">
                <p className="text-sm font-semibold mb-3 inline-flex items-center gap-2">
                  <Package className="w-4 h-4 text-slate-500" /> {t("fcl")}
                </p>
                <div className="grid grid-cols-3 gap-2">
                  <Field label={t("price_20ft")}>
                    <input type="number" step="0.01" data-testid="rf-20ft" value={form.precio_20ft} onChange={(e)=>setForm({...form, precio_20ft: +e.target.value})} className="input" />
                  </Field>
                  <Field label={t("price_40ft")}>
                    <input type="number" step="0.01" data-testid="rf-40ft" value={form.precio_40ft} onChange={(e)=>setForm({...form, precio_40ft: +e.target.value})} className="input" />
                  </Field>
                  <Field label={t("price_40hc")}>
                    <input type="number" step="0.01" data-testid="rf-40hc" value={form.precio_40hc} onChange={(e)=>setForm({...form, precio_40hc: +e.target.value})} className="input" />
                  </Field>
                </div>
              </div>

              {/* Courier fixed prices (documentos / pasaporte / goods) */}
              <div className="border border-slate-200 rounded-lg p-3">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-2">Courier · {t("cargo_col")}</p>
                <div className="grid grid-cols-3 gap-3">
                  <Field label={t("courier_documentos")}>
                    <input type="number" step="0.01" data-testid="rf-documentos" value={form.precio_documentos} onChange={(e)=>setForm({...form, precio_documentos: +e.target.value})} className="input" />
                  </Field>
                  <Field label={t("courier_pasaporte")}>
                    <input type="number" step="0.01" data-testid="rf-pasaporte" value={form.precio_pasaporte} onChange={(e)=>setForm({...form, precio_pasaporte: +e.target.value})} className="input" />
                  </Field>
                  <Field label={t("courier_goods")}>
                    <input type="number" step="0.01" data-testid="rf-goods" value={form.precio_goods} onChange={(e)=>setForm({...form, precio_goods: +e.target.value})} className="input" />
                  </Field>
                </div>
              </div>

              {/* Destination provinces offered on this route */}
              <div className="border-2 border-emerald-200 rounded-lg p-3 bg-emerald-50/40">
                <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                  <div>
                    <p className="text-sm font-semibold text-emerald-900">{t("dest_provinces")}</p>
                    <p className="text-[11px] text-emerald-800">{t("dest_provinces_hint")}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="flex items-center gap-2 text-xs font-semibold text-slate-700 select-none">
                      <input
                        type="checkbox"
                        checked={!!form.provincia_required}
                        onChange={(e)=>setForm(f => ({ ...f, provincia_required: e.target.checked }))}
                        data-testid="rf-prov-required"
                        className="w-4 h-4 accent-emerald-600"
                      />
                      {t("province_mandatory")}
                    </label>
                    <button
                      type="button"
                      onClick={()=>setForm(f => ({ ...f, provincias_destino: [...(f.provincias_destino || []), ""] }))}
                      data-testid="rf-add-prov"
                      className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold px-3 py-1.5 rounded flex items-center gap-1"
                    >
                      <Plus className="w-3 h-3" /> {t("add_province")}
                    </button>
                  </div>
                </div>
                {destProvinceCatalog.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {destProvinceCatalog
                      .filter(p => !(form.provincias_destino || []).includes(p.name))
                      .map(p => (
                        <button
                          key={p.name}
                          type="button"
                          data-testid={`rf-prov-quick-${p.name}`}
                          onClick={()=>setForm(f => ({ ...f, provincias_destino: [...(f.provincias_destino || []), p.name] }))}
                          className="text-[11px] px-2 py-1 rounded-full border border-emerald-300 bg-white text-emerald-800 hover:bg-emerald-600 hover:text-white transition-colors"
                        >
                          + {provinceLabel(p, lang)}
                        </button>
                      ))}
                  </div>
                )}
                {(form.provincias_destino || []).length === 0 ? (
                  <p className="text-[11px] text-slate-500 py-1 italic">— {t("no_dest_provinces")} —</p>
                ) : (
                  <div className="space-y-2">
                    <div className="hidden sm:grid grid-cols-[1fr_92px_92px_78px_28px] gap-2 text-[10px] font-bold uppercase tracking-wider text-emerald-800 px-1">
                      <span>{t("province")}</span>
                      <span className="text-end">{t("surcharge_fixed")}</span>
                      <span className="text-end">{t("surcharge_unit")}</span>
                      <span className="text-end">{t("extra_days")}</span>
                      <span />
                    </div>
                    {(form.provincias_destino || []).map((p, idx) => {
                      const price = (form.provincia_pricing || []).find(x => x.provincia === p) || {};
                      const setPrice = (k, v) => setForm(f => {
                        const list = [...(f.provincia_pricing || [])];
                        const i = list.findIndex(x => x.provincia === p);
                        const next = { provincia: p, recargo_fijo: 0, recargo_unidad: 0, dias_extra: 0, ...(i >= 0 ? list[i] : {}), [k]: v };
                        if (i >= 0) list[i] = next; else list.push(next);
                        return { ...f, provincia_pricing: list };
                      });
                      return (
                        <div key={p || `prov-${idx}`} className="grid grid-cols-1 sm:grid-cols-[1fr_92px_92px_78px_28px] gap-2 items-center" data-testid={`rf-prov-${idx}`}>
                          <input
                            value={p}
                            onChange={(e)=>setForm(f => {
                              const l=[...(f.provincias_destino||[])]; const old = l[idx]; l[idx]=e.target.value;
                              const pricing = (f.provincia_pricing||[]).map(x => x.provincia === old ? { ...x, provincia: e.target.value } : x);
                              return {...f, provincias_destino: l, provincia_pricing: pricing};
                            })}
                            data-testid={`rf-prov-${idx}-name`}
                            placeholder={t("province")}
                            className="input"
                            list="rf-prov-list"
                          />
                          <input type="number" step="0.01" value={price.recargo_fijo ?? 0} onChange={(e)=>setPrice("recargo_fijo", +e.target.value)} data-testid={`rf-prov-${idx}-fijo`} className="input tabular-nums text-end" title={t("surcharge_fixed")} />
                          <input type="number" step="0.01" value={price.recargo_unidad ?? 0} onChange={(e)=>setPrice("recargo_unidad", +e.target.value)} data-testid={`rf-prov-${idx}-unidad`} className="input tabular-nums text-end" title={t("surcharge_unit")} />
                          <input type="number" min="0" value={price.dias_extra ?? 0} onChange={(e)=>setPrice("dias_extra", +e.target.value)} data-testid={`rf-prov-${idx}-dias`} className="input tabular-nums text-end" title={t("extra_days")} />
                          <button
                            type="button"
                            onClick={()=>setForm(f => ({
                              ...f,
                              provincias_destino: (f.provincias_destino||[]).filter((_, i) => i !== idx),
                              provincia_pricing: (f.provincia_pricing||[]).filter(x => x.provincia !== p),
                            }))}
                            data-testid={`rf-prov-${idx}-remove`}
                            className="p-1.5 text-red-500 hover:bg-red-50 rounded justify-self-start sm:justify-self-auto"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      );
                    })}
                    <p className="text-[10px] text-emerald-800">{t("surcharge_hint")}</p>
                  </div>
                )}
                <datalist id="rf-prov-list">
                  {destProvinceCatalog.map(p => <option key={p.name} value={p.name}>{provinceLabel(p, lang)}</option>)}
                </datalist>
              </div>

              {/* Dynamic Merchandise Types (admin-configurable) */}
              <div className="border-2 border-indigo-200 rounded-lg p-3 bg-indigo-50/40">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="text-sm font-semibold text-indigo-900">{t("merchandise_types")}</p>
                    <p className="text-[11px] text-indigo-800">{t("merchandise_types_hint")}</p>
                  </div>
                  <button
                    type="button"
                    onClick={addMerchType}
                    data-testid="rf-add-merch"
                    className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold px-3 py-1.5 rounded flex items-center gap-1"
                  >
                    <Plus className="w-3 h-3" /> {t("add_merchandise_type")}
                  </button>
                </div>
                {(form.merchandise_types || []).length === 0 && (
                  <p className="text-[11px] text-slate-500 py-2 italic">— {t("no_merchandise_types")} —</p>
                )}
                <div className="space-y-2">
                  {(form.merchandise_types || []).map((mt, idx) => (
                    <div key={mt.nombre ? `mt-${mt.nombre}` : `mt-idx-${idx}`} className="bg-white border border-indigo-200 rounded-md p-3 grid grid-cols-1 md:grid-cols-[1fr_120px_130px_130px_36px] gap-2 items-end" data-testid={`rf-merch-${idx}`}>
                      <Field label={t("merch_name")}>
                        <input
                          required
                          value={mt.nombre}
                          onChange={(e)=>updateMerchType(idx, "nombre", e.target.value)}
                          data-testid={`rf-merch-${idx}-nombre`}
                          placeholder={t("merch_name_ph")}
                          className="input"
                        />
                      </Field>
                      <Field label={`${t("merch_surcharge")} (${form.moneda})`}>
                        <input
                          type="number"
                          step="0.01"
                          value={mt.precio}
                          onChange={(e)=>updateMerchType(idx, "precio", +e.target.value)}
                          data-testid={`rf-merch-${idx}-precio`}
                          className="input"
                        />
                      </Field>
                      <Field label={t("merch_charge_type") || "Tipo"}>
                        <select
                          value={mt.tipo || "envio"}
                          onChange={(e)=>updateMerchType(idx, "tipo", e.target.value)}
                          data-testid={`rf-merch-${idx}-tipo`}
                          className="input"
                        >
                          <option value="envio">{t("merch_per_shipment") || "Por envío"}</option>
                          <option value="kilo">{t("merch_per_kg") || "Por kilo"}</option>
                        </select>
                      </Field>
                      <label className="flex items-center gap-2 text-xs font-semibold text-slate-700 pb-2 select-none">
                        <input
                          type="checkbox"
                          checked={!!mt.is_mandatory}
                          onChange={(e)=>updateMerchType(idx, "is_mandatory", e.target.checked)}
                          data-testid={`rf-merch-${idx}-mandatory`}
                          className="w-4 h-4 accent-indigo-600"
                        />
                        {t("mandatory")}
                      </label>
                      <button
                        type="button"
                        onClick={()=>removeMerchType(idx)}
                        data-testid={`rf-merch-${idx}-remove`}
                        className="p-1.5 text-red-500 hover:bg-red-50 rounded mb-1"
                        title={t("delete")}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="border-2 border-red-200 rounded-lg p-3 bg-red-50/40">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="text-sm font-semibold text-red-900">{t("branch_pricing")}</p>
                    <p className="text-[11px] text-red-800">{t("sucursal_prices_hint")}</p>
                  </div>
                  <button type="button" onClick={addBranchPrice} data-testid="rf-add-branch" className="bg-red-600 hover:bg-red-700 text-white text-xs font-semibold px-3 py-1.5 rounded flex items-center gap-1">
                    <Plus className="w-3 h-3" /> {t("add_branch_price")}
                  </button>
                </div>
                {(form.sucursal_pricing || []).length === 0 && (
                  <p className="text-[11px] text-slate-500 py-2 italic">— {t("no_data") || "Sin precios especiales por sucursal"} —</p>
                )}
                <div className="space-y-3">
                  {(form.sucursal_pricing || []).map((sp, idx) => (
                    <div key={sp.oficina ? `sp-${sp.oficina}` : `sp-idx-${idx}`} className="bg-white border border-red-200 rounded-md p-3 space-y-2" data-testid={`rf-branch-${idx}`}>
                      <div className="flex items-center gap-2">
                        <select required value={sp.oficina} onChange={(e)=>updateBranchPrice(idx, "oficina", e.target.value)} data-testid={`rf-branch-${idx}-oficina`} className="input flex-1">
                          <option value="">— {t("select_branch")} —</option>
                          {oficinas.map(o => <option key={o} value={o}>{o}</option>)}
                        </select>
                        <button type="button" onClick={()=>removeBranchPrice(idx)} className="p-1.5 text-red-500 hover:bg-red-50 rounded">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="grid grid-cols-4 gap-2">
                        <Field label={t("price_base")}><input type="number" step="0.01" value={sp.precio_base} onChange={(e)=>updateBranchPrice(idx, "precio_base", +e.target.value)} className="input" /></Field>
                        <Field label="KG"><input type="number" step="0.01" value={sp.precio_kg} onChange={(e)=>updateBranchPrice(idx, "precio_kg", +e.target.value)} className="input" /></Field>
                        <Field label="CBM"><input type="number" step="0.01" value={sp.precio_cbm} onChange={(e)=>updateBranchPrice(idx, "precio_cbm", +e.target.value)} className="input" /></Field>
                        <Field label={t("cargo_pallets")}><input type="number" step="0.01" value={sp.precio_pallet} onChange={(e)=>updateBranchPrice(idx, "precio_pallet", +e.target.value)} className="input" /></Field>
                        <Field label="20ft"><input type="number" step="0.01" value={sp.precio_20ft} onChange={(e)=>updateBranchPrice(idx, "precio_20ft", +e.target.value)} className="input" /></Field>
                        <Field label="40ft"><input type="number" step="0.01" value={sp.precio_40ft} onChange={(e)=>updateBranchPrice(idx, "precio_40ft", +e.target.value)} className="input" /></Field>
                        <Field label="40HC"><input type="number" step="0.01" value={sp.precio_40hc} onChange={(e)=>updateBranchPrice(idx, "precio_40hc", +e.target.value)} className="input" /></Field>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <Field label={t("observations")}>
              <textarea data-testid="rf-obs" rows="2" value={form.observaciones} onChange={(e)=>setForm({...form, observaciones: e.target.value})} className="input" placeholder="Notas visibles al cliente en la cotización" />
            </Field>

            <div className="flex gap-3 pt-2">
              <button type="button" onClick={()=>setShowForm(false)} className="flex-1 border border-slate-300 py-2.5 rounded-lg font-semibold text-sm hover:bg-slate-50">{t("cancel")}</button>
              <button type="submit" data-testid="rf-submit" className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2.5 rounded-lg font-semibold text-sm">{t("save")}</button>
            </div>
          </form>
          <style>{`.input{width:100%;padding:.55rem .75rem;border:1px solid #cbd5e1;border-radius:.5rem;font-size:.875rem;outline:none;background:white}.input:focus{border-color:#2563eb;box-shadow:0 0 0 3px rgba(37,99,235,.15)}`}</style>
        </div>
      )}
    </div>
  );
}

function TierEditor({ testidPrefix, Icon, label, unit, base, brackets, setBase, setBrackets }) {
  const addTier = () => setBrackets([...(brackets || []), { desde: 0, precio: 0 }]);
  const updateTier = (i, k, v) => {
    const next = [...brackets];
    next[i] = { ...next[i], [k]: +v };
    setBrackets(next);
  };
  const removeTier = (i) => setBrackets(brackets.filter((_, idx) => idx !== i));

  return (
    <div className="border border-slate-200 rounded-lg p-3 bg-slate-50">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-md bg-white border border-slate-200 flex items-center justify-center"><Icon className="w-3.5 h-3.5 text-slate-600" /></div>
          <span className="text-sm font-semibold">{label}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Base</span>
          <input type="number" step="0.01" data-testid={`rf-${testidPrefix}`} value={base} onChange={(e)=>setBase(+e.target.value)} className="w-24 px-2 py-1 border border-slate-300 rounded-md text-sm bg-white" />
        </div>
      </div>

      {brackets && brackets.length > 0 && (
        <div className="space-y-1.5 mb-2">
          {/* Show each bracket as a closed "from A to B → price" range.
              The "to" of each bracket is derived from the next bracket's
              "desde", so the admin only sets the starting threshold. The
              last bracket is open-ended ("Y kg o más"). */}
          {[...brackets]
            .map((b, idx) => ({ ...b, __i: idx }))
            .sort((a, b) => (+a.desde || 0) - (+b.desde || 0))
            .map((b, sortedIdx, arr) => {
              const i = b.__i;
              const hasta = sortedIdx < arr.length - 1 ? +arr[sortedIdx + 1].desde : null;
              return (
                <div key={`tier-${i}`} className="flex items-center gap-2 text-xs flex-wrap" data-testid={`rf-${testidPrefix}-tier-${i}`}>
                  <TrendingUp className="w-3 h-3 text-slate-400" />
                  <span className="text-slate-500">De</span>
                  <input type="number" step="0.01" min="0"
                    value={b.desde}
                    onChange={(e)=>updateTier(i, "desde", e.target.value)}
                    placeholder={`${unit}`}
                    className="w-20 px-2 py-1 border border-slate-300 rounded-md bg-white" />
                  <span className="text-slate-500">a</span>
                  {hasta !== null ? (
                    <span className="w-20 px-2 py-1 border border-slate-200 rounded-md bg-slate-100 tabular-nums text-slate-700 text-center">{hasta}</span>
                  ) : (
                    <span className="w-20 px-2 py-1 border border-slate-200 rounded-md bg-slate-100 tabular-nums text-slate-500 text-center italic">∞</span>
                  )}
                  <span className="text-slate-500">{unit} →</span>
                  <span className="text-slate-500">$</span>
                  <input type="number" step="0.01" min="0"
                    value={b.precio}
                    onChange={(e)=>updateTier(i, "precio", e.target.value)}
                    placeholder="$"
                    className="w-24 px-2 py-1 border border-slate-300 rounded-md bg-white" />
                  <span className="text-slate-500">/{unit}</span>
                  <button type="button" onClick={()=>removeTier(i)} className="ms-auto p-1 text-red-500 hover:bg-red-50 rounded">
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              );
            })}
        </div>
      )}

      <button
        type="button"
        onClick={addTier}
        data-testid={`rf-${testidPrefix}-add-tier`}
        className="text-xs font-semibold text-red-600 hover:underline flex items-center gap-1"
      >
        <Plus className="w-3 h-3" /> Añadir escalón
      </button>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-600 mb-1">{label}</label>
      {children}
    </div>
  );
}

function PriceBlock({ Icon, label, value, tone }) {
  const bg = { blue: "bg-red-50 text-red-700", indigo: "bg-indigo-50 text-indigo-700", amber: "bg-amber-50 text-amber-700" }[tone];
  return (
    <div className="border border-slate-200 rounded-lg p-3">
      <div className="flex items-center gap-2 mb-1">
        <div className={`w-6 h-6 rounded-md flex items-center justify-center ${bg}`}><Icon className="w-3.5 h-3.5" /></div>
        <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">{label}</span>
      </div>
      <div className="font-display text-xl font-bold tabular-nums text-slate-900">${(+value || 0).toFixed(2)}</div>
    </div>
  );
}
